import { useState } from 'react';
import type { PerformanceSortField } from '@domain/repositories';
import { useContainer } from '../providers/container-context';
import { useAsync, useDebounced } from '../hooks/use-async';
import { useIsMobile } from '../hooks/use-is-mobile';
import { EmptyState, ErrorState, Pager, RefreshButton, SearchInput, TableSkeleton } from '../components/ui';
import { PAGE_SIZE, formatFcfa } from '../theme/tokens';

const JOURNAL_LIMIT = 30;

export function PerformancePage(): JSX.Element {
  const container = useContainer();

  const [tab, setTab] = useState<'companies' | 'journal'>('companies');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<PerformanceSortField>('revenue');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [journalSearch, setJournalSearch] = useState('');

  const debouncedSearch = useDebounced(search);
  const debouncedJournal = useDebounced(journalSearch);

  const isMobile = useIsMobile();
  const totals = useAsync(() => container.performance.totals.execute(), [container]);
  const resource = useAsync(
    () =>
      container.performance.search.execute({
        search: debouncedSearch,
        page,
        pageSize: PAGE_SIZE,
        sortBy,
        sortDir,
      }),
    [container, debouncedSearch, page, sortBy, sortDir],
  );
  const journal = useAsync(
    () =>
      container.performance.journal.execute({
        search: debouncedJournal,
        limit: JOURNAL_LIMIT,
      }),
    [container, debouncedJournal],
  );

  const toggleSort = (field: PerformanceSortField): void => {
    if (sortBy === field) {
      setSortDir((dir) => (dir === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortBy(field);
      setSortDir('desc');
    }
    setPage(1);
  };

  const arrow = (field: PerformanceSortField): string =>
    sortBy === field ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

  const rows = resource.data?.items ?? [];
  const journalRows = journal.data ?? [];

  const t = totals.data;
  const kpis = t
    ? [
        {
          label: 'CA total net',
          value: formatFcfa(t.totalRevenue),
          sub: 'abonnements + publications',
          color: '#33506B',
        },
        {
          label: 'CA abonnements',
          value: formatFcfa(t.revenueSubscriptions),
          sub: 'business subscriptions',
          color: '#5CA989',
        },
        {
          label: 'CA publications offres',
          value: formatFcfa(t.revenueOffers),
          sub: 'user simple + business',
          color: '#D67F9E',
        },
        {
          label: 'Entreprises actives',
          value: String(t.activeCompanies),
          sub: 'comptes approuvés',
          color: '#E0A552',
        },
        {
          label: 'Mamans actives',
          value: String(t.activeUsers),
          sub: 'comptes non supprimés',
          color: '#7FB3D5',
        },
      ]
    : [];

  return (
    <>
      <div className="bbm-kpi-grid" style={{ marginBottom: 20 }}>
        {kpis.map((kpi) => (
          <div key={kpi.label} className="bbm-card">
            <div
              aria-hidden="true"
              style={{ width: 8, height: 8, borderRadius: 99, background: kpi.color, marginBottom: 10 }}
            />
            <div className="bbm-kpi__value" style={{ fontSize: 22 }}>
              {kpi.value}
            </div>
            <div className="bbm-kpi__label">{kpi.label}</div>
            <div style={{ fontSize: 11, color: '#A5B4C3', marginTop: 2 }}>{kpi.sub}</div>
          </div>
        ))}
      </div>

      <div className="bbm-tabs" style={{ marginBottom: 16 }}>
        <button
          type="button"
          className={`bbm-tab${tab === 'companies' ? ' bbm-tab--active' : ''}`}
          onClick={() => setTab('companies')}
        >
          Entreprises
        </button>
        <button
          type="button"
          className={`bbm-tab${tab === 'journal' ? ' bbm-tab--active' : ''}`}
          onClick={() => setTab('journal')}
        >
          Journal des utilisations
        </button>
      </div>

      {tab === 'companies' ? (
        <>
          <div className="bbm-toolbar">
            <div className="bbm-toolbar__start">
              <div className="bbm-toolbar__hint" style={{ maxWidth: 600 }}>
                Comptes approuvés · CA net = (abonnements + publications) × (1 − frais fournisseur) · cliquer une colonne pour trier
              </div>
              <SearchInput
                value={search}
                placeholder="Rechercher une entreprise…"
                onChange={(next) => {
                  setSearch(next);
                  setPage(1);
                }}
              />
            </div>
            <div className="bbm-toolbar__end">
              <RefreshButton onRefresh={resource.reload} loading={resource.loading} />
            </div>
          </div>

          {resource.loading ? <TableSkeleton /> : null}
          {resource.error ? (
            <ErrorState
              status={resource.error.status}
              message={resource.error.message}
              onRetry={resource.reload}
            />
          ) : null}
          {!resource.loading && !resource.error && rows.length === 0 ? (
            <EmptyState
              title="Aucun résultat"
              body="Aucune entreprise ne correspond à cette recherche."
              actionLabel="Réinitialiser"
              onAction={() => {
                setSearch('');
                setPage(1);
              }}
            />
          ) : null}

          {!resource.loading && !resource.error && rows.length > 0 && resource.data ? (
            <>
              {isMobile ? (
                <div className="bbm-card-stack">
                  {rows.map((row) => (
                    <div key={row.id} className="bbm-mobile-card">
                      <div className="bbm-mobile-card__head">
                        <div>
                          <div className="bbm-mobile-card__title">{row.name}</div>
                          <div className="bbm-mobile-card__sub">{row.city || '—'}</div>
                        </div>
                      </div>
                      <div className="bbm-mobile-card__fields">
                        <div className="bbm-mobile-card__field">
                          <span className="bbm-mobile-card__label">Formule</span>
                          <span>{row.formulaLabel || '—'}</span>
                        </div>
                        <div className="bbm-mobile-card__field">
                          <span className="bbm-mobile-card__label">Offres</span>
                          <span>{row.offersCount}</span>
                        </div>
                        <div className="bbm-mobile-card__field">
                          <span className="bbm-mobile-card__label">CA abonnements</span>
                          <span style={{ color: row.revenueSubscriptions > 0 ? '#2A6B47' : '#8A95A3', fontWeight: 700 }}>
                            {row.revenueSubscriptions > 0 ? formatFcfa(row.revenueSubscriptions) : '—'}
                          </span>
                        </div>
                        <div className="bbm-mobile-card__field">
                          <span className="bbm-mobile-card__label">CA publications</span>
                          <span style={{ color: row.revenueOffers > 0 ? '#A8487B' : '#8A95A3', fontWeight: 700 }}>
                            {row.revenueOffers > 0 ? formatFcfa(row.revenueOffers) : '—'}
                          </span>
                        </div>
                        <div className="bbm-mobile-card__field">
                          <span className="bbm-mobile-card__label">CA total</span>
                          <span style={{ color: row.revenue > 0 ? '#33506B' : '#8A95A3', fontWeight: 800 }}>
                            {row.revenue > 0 ? formatFcfa(row.revenue) : '—'}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bbm-panel">
                  <div className="bbm-row bbm-row--head">
                    <button
                      type="button"
                      className={`bbm-sortable${sortBy === 'name' ? ' bbm-sortable--active' : ''}`}
                      style={{ flex: '1 1 200px', minWidth: 140 }}
                      onClick={() => toggleSort('name')}
                    >
                      Entreprise{arrow('name')}
                    </button>
                    <div style={{ width: 100 }}>Ville</div>
                    <div style={{ width: 150 }}>Formule</div>
                    <button
                      type="button"
                      className={`bbm-sortable${sortBy === 'offersCount' ? ' bbm-sortable--active' : ''}`}
                      style={{ width: 80 }}
                      onClick={() => toggleSort('offersCount')}
                    >
                      Offres{arrow('offersCount')}
                    </button>
                    <div style={{ width: 140, color: '#5CA989', fontWeight: 700 }}>CA abonnements</div>
                    <div style={{ width: 140, color: '#D67F9E', fontWeight: 700 }}>CA publications</div>
                    <button
                      type="button"
                      className={`bbm-sortable${sortBy === 'revenue' ? ' bbm-sortable--active' : ''}`}
                      style={{ width: 130 }}
                      onClick={() => toggleSort('revenue')}
                    >
                      CA total{arrow('revenue')}
                    </button>
                  </div>

                  {rows.map((row) => (
                    <div key={row.id} className="bbm-row">
                      <div style={{ flex: '1 1 200px', minWidth: 140 }}>
                        <div className="bbm-cell-strong">{row.name}</div>
                        <div className="bbm-cell-sub">{row.type}</div>
                      </div>
                      <div className="bbm-cell" style={{ width: 100 }}>{row.city || '—'}</div>
                      <div style={{ width: 150, fontSize: 12.5, color: '#33506B', fontWeight: 600 }}>
                        {row.formulaLabel || '—'}
                      </div>
                      <div style={{ width: 80, fontSize: 15, fontWeight: 800, color: '#33506B' }}>
                        {row.offersCount}
                        <span style={{ fontSize: 11, fontWeight: 400, color: '#8A95A3', marginLeft: 2 }}>offres</span>
                      </div>
                      <div style={{ width: 140, fontSize: 13, fontWeight: 700, color: row.revenueSubscriptions > 0 ? '#2A6B47' : '#C2CDD6' }}>
                        {row.revenueSubscriptions > 0 ? formatFcfa(row.revenueSubscriptions) : '—'}
                      </div>
                      <div style={{ width: 140, fontSize: 13, fontWeight: 700, color: row.revenueOffers > 0 ? '#A8487B' : '#C2CDD6' }}>
                        {row.revenueOffers > 0 ? formatFcfa(row.revenueOffers) : '—'}
                      </div>
                      <div style={{ width: 130, fontSize: 14, fontWeight: 800, color: row.revenue > 0 ? '#33506B' : '#8A95A3' }}>
                        {row.revenue > 0 ? formatFcfa(row.revenue) : '—'}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <Pager
                page={resource.data.page}
                pageCount={resource.data.pageCount}
                total={resource.data.total}
                pageSize={resource.data.pageSize}
                onChange={setPage}
              />
            </>
          ) : null}
        </>
      ) : (
        <>
          <div className="bbm-toolbar">
            <div className="bbm-toolbar__start">
              <div className="bbm-toolbar__hint">
                Offres ayant reçu au moins un clic (appel, WhatsApp ou email)
              </div>
              <SearchInput
                value={journalSearch}
                width={300}
                placeholder="Rechercher une entreprise, une offre…"
                onChange={setJournalSearch}
              />
            </div>
            <div className="bbm-toolbar__end">
              <RefreshButton onRefresh={journal.reload} loading={journal.loading} />
            </div>
          </div>

          {journal.loading ? <TableSkeleton /> : null}
          {journal.error ? (
            <ErrorState
              status={journal.error.status}
              message={journal.error.message}
              onRetry={journal.reload}
            />
          ) : null}
          {!journal.loading && !journal.error && journalRows.length === 0 ? (
            <EmptyState
              title="Aucun résultat"
              body="Aucune utilisation ne correspond à cette recherche."
            />
          ) : null}

          {journalRows.length > 0 ? (
            isMobile ? (
              <div className="bbm-card-stack">
                {journalRows.map((tx) => (
                  <div key={tx.id} className="bbm-mobile-card">
                    <div className="bbm-mobile-card__head">
                      <div>
                        <div className="bbm-mobile-card__title">{tx.offerTitle}</div>
                        <div className="bbm-mobile-card__sub">{tx.companyName}</div>
                      </div>
                    </div>
                    <div className="bbm-mobile-card__fields">
                      <div className="bbm-mobile-card__field">
                        <span className="bbm-mobile-card__label">Clics</span>
                        <span style={{ fontWeight: 700, color: '#33506B' }}>{tx.clicks ?? 0}</span>
                      </div>
                      <div className="bbm-mobile-card__field">
                        <span className="bbm-mobile-card__label">Publication</span>
                        <span>{tx.usedAt ? new Date(tx.usedAt).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bbm-panel">
                <div className="bbm-row bbm-row--head">
                  <div style={{ width: 32, textAlign: 'center' }}>#</div>
                  <div style={{ flex: '1 1 180px', minWidth: 140 }}>Entreprise</div>
                  <div style={{ flex: '1 1 220px', minWidth: 160 }}>Offre</div>
                  <div style={{ width: 80, textAlign: 'right' }}>Clics</div>
                  <div style={{ width: 120 }}>Publication</div>
                </div>
                {journalRows.map((tx, i) => (
                  <div key={tx.id} className="bbm-row">
                    <div style={{ width: 32, textAlign: 'center', fontSize: 12, color: '#C2CDD6', fontWeight: 700 }}>
                      {i + 1}
                    </div>
                    <div className="bbm-cell bbm-truncate" style={{ flex: '1 1 180px', minWidth: 140 }}>
                      {tx.companyName}
                    </div>
                    <div className="bbm-cell bbm-truncate" style={{ flex: '1 1 220px', minWidth: 160 }}>
                      {tx.offerTitle}
                    </div>
                    <div style={{ width: 80, textAlign: 'right', fontSize: 14, fontWeight: 800, color: '#33506B' }}>
                      {tx.clicks ?? 0}
                    </div>
                    <div style={{ width: 120, fontSize: 12.5, color: '#8A95A3' }}>
                      {tx.usedAt
                        ? new Date(tx.usedAt).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })
                        : '—'}
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : null}
        </>
      )}
    </>
  );
}
